import { isApiError } from '@shigo-ui/api-client';

import { dashboardApiErrorMessage } from '../../../lib/apiErrorMessages';
import { dashboardT } from '../../../lib/messages';
import type { MadBuyLineItemGeneralForm } from './lineItemFormSchema';
import type { MadBuyLineItemMutationPayload } from './lineItemsCoreModel';
import {
  applyLineItemTargetingCsvImport,
  LINE_ITEM_TARGETING_CSV_MAX_BYTES,
  LineItemTargetingCsvKind,
  type LineItemTargetingCsvImportResult,
} from './lineItemTargetingCsvModel';

export enum LineItemTargetingCsvIssue {
  /** Network failure or 5xx: the import may or may not have committed. */
  UnknownOutcome = 'UNKNOWN_OUTCOME',
  /** Validation or request failure; nothing was imported. */
  Rejected = 'REJECTED',
}

export type LineItemTargetingCsvFailure = Readonly<{
  file: File;
  issue: LineItemTargetingCsvIssue;
  messages: readonly string[];
}>;

type SavedLineItem = { id: string; targeting?: object | null | undefined };
type SavedTargeting = Readonly<Record<string, unknown>> | null | undefined;

export interface LineItemTargetingCsvSaveDependencies<TLineItem extends SavedLineItem> {
  create: (payload: MadBuyLineItemMutationPayload) => Promise<TLineItem>;
  get: (lineItemId: string) => Promise<TLineItem>;
  importCsv: (
    lineItemId: string,
    kind: LineItemTargetingCsvKind,
    file: File,
  ) => Promise<LineItemTargetingCsvImportResult>;
  update: (lineItemId: string, payload: MadBuyLineItemMutationPayload) => Promise<TLineItem>;
}

export interface LineItemTargetingCsvSaveResult {
  failures: Partial<Record<LineItemTargetingCsvKind, LineItemTargetingCsvFailure>>;
  imported: LineItemTargetingCsvKind[];
  lineItemId: string;
  /** Saved counts: the save response patched with imports, or a reload after an unknown outcome. */
  savedTargeting: SavedTargeting;
}

export const lineItemTargetingCsvFormFields = {
  [LineItemTargetingCsvKind.ZipCodes]: 'pendingZipCodesCsv',
  [LineItemTargetingCsvKind.IpAddresses]: 'pendingIpAddressesCsv',
} as const satisfies Record<LineItemTargetingCsvKind, keyof MadBuyLineItemGeneralForm>;

/** Saves the line item, then replaces each selected ZIP/IP list from its pending CSV. */
export async function saveLineItemWithTargetingCsv<TLineItem extends SavedLineItem>({
  dependencies,
  form,
  lineItemId,
  payload,
}: {
  dependencies: LineItemTargetingCsvSaveDependencies<TLineItem>;
  form: Pick<MadBuyLineItemGeneralForm, 'pendingZipCodesCsv' | 'pendingIpAddressesCsv'>;
  lineItemId?: string | undefined;
  payload: MadBuyLineItemMutationPayload;
}): Promise<LineItemTargetingCsvSaveResult> {
  const saved = lineItemId
    ? await dependencies.update(lineItemId, payload)
    : await dependencies.create(payload);
  let summary: { targeting?: SavedTargeting } = { targeting: saved.targeting as SavedTargeting };
  const failures: LineItemTargetingCsvSaveResult['failures'] = {};
  const imported: LineItemTargetingCsvKind[] = [];

  for (const kind of Object.values(LineItemTargetingCsvKind)) {
    const file = form[lineItemTargetingCsvFormFields[kind]];
    if (!file) continue;
    if (file.size > LINE_ITEM_TARGETING_CSV_MAX_BYTES) {
      failures[kind] = {
        file,
        issue: LineItemTargetingCsvIssue.Rejected,
        messages: [dashboardT('targeting.lineItemCsv.tooLarge')],
      };
      continue;
    }
    try {
      // react-doctor-disable-next-line react-doctor/async-await-in-loop -- imports change the same line item's targeting; run them one at a time
      const result = await dependencies.importCsv(saved.id, kind, file);
      summary = applyLineItemTargetingCsvImport(summary, kind, result);
      imported.push(kind);
    } catch (error) {
      failures[kind] = { file, ...classifyImportError(error) };
    }
  }

  const needsReload = Object.values(failures).some(
    (failure) => failure.issue !== LineItemTargetingCsvIssue.Rejected,
  );
  if (needsReload) {
    try {
      summary = { targeting: (await dependencies.get(saved.id)).targeting as SavedTargeting };
    } catch {
      // Keep the last known state; the failure notice still asks to check before retrying.
    }
  }
  return { failures, imported, lineItemId: saved.id, savedTargeting: summary.targeting };
}

function classifyImportError(error: unknown): Omit<LineItemTargetingCsvFailure, 'file'> {
  if (!isApiError(error) || error.status >= 500) {
    return { issue: LineItemTargetingCsvIssue.UnknownOutcome, messages: [] };
  }
  if (error.status === 413) {
    return {
      issue: LineItemTargetingCsvIssue.Rejected,
      messages: [dashboardT('targeting.lineItemCsv.tooLarge')],
    };
  }
  const body = error.body as { error?: { messages?: unknown } } | null;
  const messages = body?.error?.messages;
  const texts = Array.isArray(messages)
    ? messages.filter(
        (message): message is string => typeof message === 'string' && !!message.trim(),
      )
    : [];
  return {
    issue: LineItemTargetingCsvIssue.Rejected,
    messages: texts.length ? texts : [dashboardApiErrorMessage(error)],
  };
}
